import React, { Component  } from "react";
import { Col, Container, Row, ButtonGroup, Button, Input, Label } from "reactstrap";
import PositionList from "./PositionList";
import ToggleStatsPositions from "./ToggleStatsPositions";
import AccountInfo from "./AccountInfo";
import NavComponent from "./Navbar";
import RiskExposureWidget from "./RiskExposureWidget";
import AppFooter from "./Footer";
import BreakdownBarChart from "./BreakdownBarChart";
import TransactionsPanel from "./TransactionsPanel";
import {
  ensurePushSubscription,
  unsubscribePushSubscription,
  updatePushNotificationPreference,
} from "../pushNotifications";

import axios from "axios";

import { POSITIONS_API_URL, ACCOUNT_INFO_API_URL, ACCOUNT_CASHFLOWS_API_URL, PREVIOUS_POSITIONS_API_URL, GET_STRATEGIES_API_URL, GET_CURRENCIES_API_URL, STATS_DISPLAY_PREFERENCES_API_URL } from "../constants";
import { getTradeBreakdowns } from "../utils/tradeBreakdowns";
import { getAuthHeaders } from "../api";
import { createDefaultStatsVisibility, normalizeStatsVisibility } from "../utils/performanceStatsRows";
import { downloadPastPerformanceWorkbook } from "../utils/pastPerformanceExport";

const lotFormatter = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 2,
});

const formatLots = (value) => `${lotFormatter.format(Number(value) || 0)} lots`;
const STATS_VISIBILITY_STORAGE_KEY = "pastPerformanceStatsVisibility";

const getInitialStatsVisibility = () => {
  const defaults = createDefaultStatsVisibility();

  if (typeof window === "undefined" || !window.localStorage) {
    return defaults;
  }

  try {
    const savedVisibility = JSON.parse(window.localStorage.getItem(STATS_VISIBILITY_STORAGE_KEY));
    if (!savedVisibility || typeof savedVisibility !== "object") {
      return defaults;
    }

    return normalizeStatsVisibility(savedVisibility);
  } catch (error) {
    return defaults;
  }
};

const saveStatsVisibility = (visibleStats) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(STATS_VISIBILITY_STORAGE_KEY, JSON.stringify(visibleStats));
  } catch (error) {
    console.error("Failed to save stats visibility:", error);
  }
};

class Home extends Component {
  state = {
    positions: [],
    previousPositions: [],
    allTradePositions: [],
    cashflows: [],
    performanceCashflows: [],
    allTradeCashflows: [],
    accountInfo: {},
    statsData: [],
    strategies: [],
    currencies: [],
    currency: 'All',
    strategy: 'All',
    startingAmount: 0,
    allTradesStartingAmount: 0,
    startDate: "",
    endDate: "",
    activeDashboardTab: "active",
    pastPerformanceView: "equity",
    notificationsEnabled: true,
    notificationsSaving: false,
    visibleStats: getInitialStatsVisibility(),
    statsFiltersOpen: false,
    statsVisibilitySaving: false,
  };

  // To hold the interval ID
  intervalId = null;

  componentDidMount() {
    this.resetState({ syncPushPreference: true });
    this.getStatsVisibilityPreference();
    this.getStrategies(); // Fetch strategies when the component mounts
    this.getCurrencies();
  }

  componentWillUnmount() {
    this.stopAutoRefresh();
  }

  getStrategies = () => {
    axios
      .get(GET_STRATEGIES_API_URL)
      .then((res) => this.setState({ strategies: res.data }));  // No need to parse
  };

  getCurrencies= () => {
    axios
      .get(GET_CURRENCIES_API_URL)
      .then((res) => this.setState({ currencies: res.data }));  // No need to parse
  };

  // Stops the auto-refresh mechanism
  stopAutoRefresh = () => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  };


  getAccountInfo = ({ syncPushPreference = false } = {}) => {
    const token = localStorage.getItem("access"); // Get token from localStorage
  
    return axios.get(ACCOUNT_INFO_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`, // Send JWT token
      },
    })
    .then((res) => {
      const notificationsEnabled = res.data.notifications_enabled !== false;

      this.setState({
        accountInfo: res.data,
        notificationsEnabled,
      });

      if (!syncPushPreference) {
        return res.data;
      }

      const pushSyncAction = notificationsEnabled
        ? ensurePushSubscription
        : unsubscribePushSubscription;

      return Promise.resolve(pushSyncAction()).catch((error) => {
        console.error("Push notification sync failed:", error);
      }).then(() => res.data);
    })
    .catch(err => console.error("Error fetching account info:", err));
  };

  getPositions = () => {
    axios.get(POSITIONS_API_URL, getAuthHeaders()).then(res => this.setState({ positions: JSON.parse(res.data) }));
  };

  getCashflows = () => {
    axios.get(ACCOUNT_CASHFLOWS_API_URL, getAuthHeaders()).then((res) => this.setState({ cashflows: res.data }));
  };

  getPreviousPositions = (months = 3) => {
    axios
      .get(`${PREVIOUS_POSITIONS_API_URL}?months=${months}&strategy=${this.state.strategy}&currency=${this.state.currency}`, getAuthHeaders())
      .then(res => {
        const positionsData = JSON.parse(res.data);
        this.setState({ 
          previousPositions: positionsData.positions,
          startingAmount: positionsData.startAmount || 0,
          performanceCashflows: positionsData.cashflows || [],
         })
      });
  };

  getTodayDateString = () => {
    return new Date().toISOString().split("T")[0];
  };

  getEarliestTradeDate = (positions = []) => {
    if (!positions.length) {
      return this.getTodayDateString();
    }

    const earliestTrade = [...positions].sort((a, b) => {
      const aTime = new Date(a.time_open || a.open_time || a.time || a.close_time || 0).getTime();
      const bTime = new Date(b.time_open || b.open_time || b.time || b.close_time || 0).getTime();
      return aTime - bTime;
    })[0];

    const earliestDateValue = earliestTrade?.time_open || earliestTrade?.open_time || earliestTrade?.time || earliestTrade?.close_time;
    if (!earliestDateValue) {
      return this.getTodayDateString();
    }

    return String(earliestDateValue).split(" ")[0].split("T")[0];
  };

  getAllTradePositions = () => {
    axios
      .get(`${PREVIOUS_POSITIONS_API_URL}?months=1000&strategy=All&currency=All`, getAuthHeaders())
      .then((res) => {
        const positionsData = JSON.parse(res.data);
        const defaultStartDate = this.getEarliestTradeDate(positionsData.positions);
        const defaultEndDate = this.getTodayDateString();

        this.setState((prevState) => {
          const startDate = prevState.startDate || defaultStartDate;
          const endDate = prevState.endDate || defaultEndDate;

          return {
            allTradePositions: positionsData.positions,
            allTradesStartingAmount: positionsData.startAmount || 0,
            allTradeCashflows: positionsData.cashflows || [],
            startDate,
            endDate,
          };
        }, () => {
          if (this.state.startDate && this.state.endDate) {
            this.getPreviousPositionsDate(this.state.startDate, this.state.endDate);
          }
        });
      });
  };

  getPreviousPositionsDate = (start, end) => {
    axios
      .get(`${PREVIOUS_POSITIONS_API_URL}?start=${start}&end=${end}&strategy=${this.state.strategy}&currency=${this.state.currency}`, getAuthHeaders())
      .then(res => {
        const positionsData = JSON.parse(res.data);
        this.setState({ 
          previousPositions: positionsData.positions,
          startingAmount: positionsData.startAmount || 0,
          performanceCashflows: positionsData.cashflows || [],
         })
      });
  };

  handleDateChange = (field, value) => {
    this.setState({ [field]: value }, () => {
      if (!this.state.startDate || !this.state.endDate) {
        return;
      }

      this.getPreviousPositionsDate(this.state.startDate, this.state.endDate);
    });
  };

  resetState = ({ syncPushPreference = false } = {}) => {
    this.getPositions();
    this.getAccountInfo({ syncPushPreference });
    this.getAllTradePositions();
    this.getCashflows();
    if (this.state.startDate && this.state.endDate) {
      this.getPreviousPositionsDate(this.state.startDate, this.state.endDate);
    }
    this.setState({ strategies: [] });
  };

  handleStrategyChange = (strategy) => {
    this.setState({strategy: strategy}, () => {
      if (!this.state.startDate || !this.state.endDate) {
        return;
      }

      this.getPreviousPositionsDate(this.state.startDate, this.state.endDate);
    });
  }

  handleCurrencyChange = (currency) => {
    this.setState({currency: currency}, () => {
      if (!this.state.startDate || !this.state.endDate) {
        return;
      }

      this.getPreviousPositionsDate(this.state.startDate, this.state.endDate);
    });
  }

  handleSubmit = (e) => {
    e.preventDefault();
    if(!this.state.startDate || !this.state.endDate){
      return;
    }
    this.getPreviousPositionsDate(this.state.startDate, this.state.endDate);
  }

  setDashboardTab = (activeDashboardTab) => {
    this.setState({ activeDashboardTab });
  };

  setPastPerformanceView = (pastPerformanceView) => {
    this.setState((prevState) => ({
      pastPerformanceView,
      statsFiltersOpen: pastPerformanceView === "stats" ? prevState.statsFiltersOpen : false,
    }));
  };

  toggleStatsFilters = () => {
    this.setState((prevState) => ({
      statsFiltersOpen: !prevState.statsFiltersOpen,
    }));
  };

  handleNotificationToggle = async (enabled) => {
    this.setState({ notificationsSaving: true });

    try {
      const data = await updatePushNotificationPreference(enabled);
      const notificationsEnabled = data.enabled !== false;

      this.setState((prevState) => ({
        notificationsEnabled,
        accountInfo: {
          ...prevState.accountInfo,
          notifications_enabled: notificationsEnabled,
        },
      }));

      if (notificationsEnabled) {
        await ensurePushSubscription();
      } else {
        await unsubscribePushSubscription();
      }
    } catch (error) {
      console.error("Failed to update notification preference:", error);
    } finally {
      this.setState({ notificationsSaving: false });
    }
  };

  handleStatVisibilityChange = (statKey, visible) => {
    this.setState((prevState) => {
      const visibleStats = normalizeStatsVisibility({
        ...prevState.visibleStats,
        [statKey]: visible,
      });

      saveStatsVisibility(visibleStats);

      return { visibleStats };
    }, () => this.saveStatsVisibilityPreference(this.state.visibleStats));
  };

  getStatsVisibilityPreference = () => {
    axios
      .get(STATS_DISPLAY_PREFERENCES_API_URL, getAuthHeaders())
      .then((res) => {
        const visibleStats = normalizeStatsVisibility(res.data?.visible_stats);
        saveStatsVisibility(visibleStats);
        this.setState({ visibleStats });
      })
      .catch((error) => {
        console.error("Failed to load stats visibility preference:", error);
      });
  };

  saveStatsVisibilityPreference = (visibleStats) => {
    this.setState({ statsVisibilitySaving: true });

    axios
      .patch(
        STATS_DISPLAY_PREFERENCES_API_URL,
        { visible_stats: visibleStats },
        getAuthHeaders(),
      )
      .catch((error) => {
        console.error("Failed to save stats visibility preference:", error);
      })
      .finally(() => {
        this.setState({ statsVisibilitySaving: false });
      });
  };

  handleExportPastPerformance = () => {
    downloadPastPerformanceWorkbook({
      positions: this.state.previousPositions,
      accountInfo: this.state.accountInfo,
      startingAmount: this.state.startingAmount,
      cashflows: this.state.performanceCashflows,
      startDate: this.state.startDate,
      endDate: this.state.endDate,
      visibleStats: this.state.visibleStats,
    });
  };
  

  renderStrategyOptions = () => {
    const strategies = Array.isArray(this.state.strategies) ? this.state.strategies : [];

    return (
      <Input
        id="strategySelect"
        name="strategies"
        type="select"
        onChange={(e) => this.handleStrategyChange(e.target.value)}
        className="algo-hover"
      >
        <option value="All">All</option>
        {strategies.map((strategy) => (
          <option
            key={strategy.strategy_id}
            value={strategy.name}
          >
            {strategy.name}
          </option>
        ))}
      </Input>
    );
  };

  renderCurrencyOptions = () => {
    const currencies = Array.isArray(this.state.currencies) ? this.state.currencies : [];

    return (
      <Input
        id="currencySelect"
        name="currencies"
        type="select"
        onChange={(e) => this.handleCurrencyChange(e.target.value)}
        className="algo-hover"
      >
        <option value="All">All</option>
        {currencies.map((currency) => (
          <option
            key={currency.pair_id}
            value={currency.pair}
          >
            {currency.pair}
          </option>
        ))}
      </Input>
    );
  };

  renderSettings = () => {
    const { notificationsEnabled, notificationsSaving } = this.state;

    return (
      <div className="settings-panel app-widget-panel">
        <div className="settings-panel__header">
          <h3 className="settings-panel__title app-widget-title">Settings</h3>
        </div>

        <div className="settings-section settings-section--split">
          <div>
            <h4 className="settings-section__title">Trade Notifications</h4>
            <span className="settings-section__status">
              {notificationsSaving ? "Saving..." : notificationsEnabled ? "On" : "Off"}
            </span>
          </div>
          <div className="form-check form-switch settings-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              aria-label="Toggle trade notifications"
              checked={notificationsEnabled}
              disabled={notificationsSaving}
              onChange={(event) => this.handleNotificationToggle(event.target.checked)}
            />
          </div>
        </div>

        <div className="settings-section settings-section--split">
          <h4 className="settings-section__title">Reset Password</h4>
          <Button
            tag="a"
            href="/forgot-password"
            className="brand-action-button settings-reset-button"
          >
            Reset Password
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const {
      activeDashboardTab,
      pastPerformanceView,
      startDate,
      endDate,
      visibleStats,
      statsFiltersOpen,
      statsVisibilitySaving,
    } = this.state;
    const tradeBreakdowns = getTradeBreakdowns(
      this.state.previousPositions,
      this.state.startingAmount,
      this.state.performanceCashflows,
    );

    return (
      <Container className="contain-main dashboard-page">
        <NavComponent
          activeDashboardTab={activeDashboardTab}
          setDashboardTab={this.setDashboardTab}
        />

        {activeDashboardTab === "active" && (
          <>
            <AccountInfo
              accountInfo={this.state.accountInfo}
              allTradePositions={this.state.allTradePositions}
              allTradesStartingAmount={this.state.allTradesStartingAmount}
              cashflows={this.state.allTradeCashflows}
            />
            <PositionList
              positions={this.state.positions}
              resetState={this.resetState}
              title="Active Trades"
            />
            <RiskExposureWidget
              positions={this.state.positions}
              accountInfo={this.state.accountInfo}
            />
          </>
        )}

        {activeDashboardTab === "past" && (
          <>
            <div className="dashboard-control-panel app-widget-panel dashboard-control-panel--stacked">
              <div className="dashboard-control-section">
                <div className="dashboard-control-panel__header">
                  <h3 className="dashboard-control-panel__title app-widget-title">Date Range</h3>
                </div>
                <Row className="gy-2">
                  <Col xs={12} md={6} className="d-flex align-items-center dashboard-filter-field">
                    <Label for="startDate" className="mb-0 dashboard-filter-label">Start Date</Label>
                    <Input
                      id="startDate"
                      name="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => this.handleDateChange("startDate", e.target.value)}
                    />
                  </Col>

                  <Col xs={12} md={6} className="d-flex align-items-center dashboard-filter-field">
                    <Label for="endDate" className="mb-0 dashboard-filter-label">End Date</Label>
                    <Input
                      id="endDate"
                      name="endDate"
                      type="date"
                      value={endDate}
                      onChange={(e) => this.handleDateChange("endDate", e.target.value)}
                    />
                  </Col>
                </Row>
              </div>

              {/* <div className="dashboard-control-section">
                <div className="dashboard-control-panel__header">
                  <h3 className="dashboard-control-panel__title app-widget-title">Filters</h3>
                </div>
                <Row className="gy-2">
                  <Col xs={12} md={6} className="d-flex align-items-center dashboard-filter-field">
                    <Label for="strategies" className="mb-0 dashboard-filter-label">
                      Forex Pair
                    </Label>
                    {this.renderCurrencyOptions()}
                  </Col>
                  <Col xs={12} md={6} className="d-flex align-items-center dashboard-filter-field">
                    <Label for="strategies" className="mb-0 dashboard-filter-label">
                      Trading Strategy
                    </Label>
                    {this.renderStrategyOptions()}
                  </Col>
                </Row>
              </div> */}

              <div className="dashboard-control-section">
                <div className="dashboard-control-panel__header">
                  <h3 className="dashboard-control-panel__title app-widget-title">View</h3>
                </div>
                <div className="dashboard-view-controls">
                  <ButtonGroup className="dashboard-mode-group">
                    <Button
                      className="dashboard-filter-button"
                      onClick={() => this.setPastPerformanceView("equity")}
                      active={pastPerformanceView === "equity"}
                    >
                      Overview
                    </Button>
                    <Button
                      className="dashboard-filter-button"
                      onClick={() => this.setPastPerformanceView("stats")}
                      active={pastPerformanceView === "stats"}
                    >
                      Stats
                    </Button>
                    <Button
                      className="dashboard-filter-button"
                      onClick={() => this.setPastPerformanceView("trades")}
                      active={pastPerformanceView === "trades"}
                    >
                      Trades
                    </Button>
                  </ButtonGroup>
                  <Button
                    className="dashboard-export-button"
                    type="button"
                    onClick={this.handleExportPastPerformance}
                  >
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                    >
                      <path
                        d="M12 3v12m0 0 4-4m-4 4-4-4M5 19h14"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>Export</span>
                  </Button>
                </div>
              </div>
            </div>
            {pastPerformanceView === "equity" && (
              <>
                <ToggleStatsPositions
                  data={this.state.statsData}
                  positions={this.state.previousPositions}
                  accountInfo={this.state.accountInfo}
                  startingAmount={this.state.startingAmount}
                  cashflows={this.state.performanceCashflows}
                  resetState={this.resetState}
                  activeTab={pastPerformanceView}
                  visibleStats={visibleStats}
                  statsFiltersOpen={statsFiltersOpen}
                  statsVisibilitySaving={statsVisibilitySaving}
                  onToggleStatsFilters={this.toggleStatsFilters}
                  onStatVisibilityChange={this.handleStatVisibilityChange}
                />
                <Row className="g-4 dashboard-breakdown-grid">
                  <Col xs={12} xl={6}>
                    <BreakdownBarChart
                      title="Monthly Net P/L / Drawdown"
                      labels={tradeBreakdowns.monthlyLabels}
                      datasets={[
                        {
                          label: "Net P/L",
                          data: tradeBreakdowns.monthlyProfitValues,
                          backgroundColor: "rgba(44, 156, 145, 0.78)",
                        },
                        {
                          label: "Drawdown",
                          data: tradeBreakdowns.monthlyDrawdownValues,
                          backgroundColor: "rgba(255, 123, 114, 0.72)",
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={12} xl={6}>
                    <BreakdownBarChart
                      title="Trades By Pair"
                      headerMeta={`Total trades: ${tradeBreakdowns.totalTrades}`}
                      labels={tradeBreakdowns.pairTradeLabels}
                      radar
                      tooltipLabelFormatter={(context) => {
                        const tradeCount = context.parsed.r || 0;
                        const volume = formatLots(tradeBreakdowns.pairTradeVolumeValues[context.dataIndex] || 0);
                        const tradeLabel = tradeCount === 1 ? "trade" : "trades";
                        return `${context.label}: ${tradeCount} ${tradeLabel} (${volume})`;
                      }}
                      datasets={[
                        {
                          label: "Trades",
                          data: tradeBreakdowns.pairTradeCountValues,
                          backgroundColor: "rgba(111, 180, 255, 0.26)",
                          borderColor: "rgba(111, 180, 255, 0.92)",
                          pointBackgroundColor: "rgba(111, 180, 255, 0.92)",
                          pointBorderColor: "#d7ecff",
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={12} xl={6}>
                    <BreakdownBarChart
                      title="Net Profit By Strategy"
                      labels={tradeBreakdowns.strategyLabels}
                      showPercentAxis
                      datasets={[
                        {
                          label: "Net Profit",
                          data: tradeBreakdowns.strategyProfitValues,
                          backgroundColor: "rgba(44, 156, 145, 0.78)",
                        },
                        {
                          type: "line",
                          label: "Win Ratio",
                          data: tradeBreakdowns.strategyWinRatioValues,
                          borderColor: "#f6c177",
                          backgroundColor: "#f6c177",
                          yAxisID: "yPercent",
                        },
                      ]}
                    />
                  </Col>
                  <Col xs={12} xl={6}>
                    <BreakdownBarChart
                      title="Net Profit By Pair"
                      labels={tradeBreakdowns.symbolLabels}
                      showPercentAxis
                      datasets={[
                        {
                          label: "Net Profit",
                          data: tradeBreakdowns.symbolProfitValues,
                          backgroundColor: "rgba(44, 156, 145, 0.78)",
                        },
                        {
                          type: "line",
                          label: "Win Ratio",
                          data: tradeBreakdowns.symbolWinRatioValues,
                          borderColor: "#f6c177",
                          backgroundColor: "#f6c177",
                          yAxisID: "yPercent",
                        },
                      ]}
                    />
                  </Col>
                </Row>
              </>
            )}
            {pastPerformanceView !== "equity" && (
              <ToggleStatsPositions
                data={this.state.statsData}
                positions={this.state.previousPositions}
                accountInfo={this.state.accountInfo}
                startingAmount={this.state.startingAmount}
                cashflows={this.state.performanceCashflows}
                resetState={this.resetState}
                activeTab={pastPerformanceView}
                visibleStats={visibleStats}
                statsFiltersOpen={statsFiltersOpen}
                statsVisibilitySaving={statsVisibilitySaving}
                onToggleStatsFilters={this.toggleStatsFilters}
                onStatVisibilityChange={this.handleStatVisibilityChange}
              />
            )}
          </>
        )}

        {activeDashboardTab === "transactions" && (
          <TransactionsPanel cashflows={this.state.cashflows} />
        )}

        {activeDashboardTab === "settings" && this.renderSettings()}

        <AppFooter />
      </Container>
    );
  }
}

export default Home;
